import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import '@/pages/home/home.css';
import SiteNav from '@/components/feature/SiteNav';
import VisitFooter from '@/pages/home/components/VisitFooter';
import { products } from '@/mocks/products';
import { cdn } from '@/config/cdn';
import ProductGallery from './components/ProductGallery';
import PhotoMotion from '@/components/feature/PhotoMotion';
import ProductInfo from './components/ProductInfo';
import { usePickupBag } from '@/hooks/usePickupBag';

export default function ProductPage() {
  const { slug } = useParams();
  const product = products.find((p) => p.slug === slug);
  const [qty, setQty] = useState(1);
  const { addItem, setBagOpen } = usePickupBag();
  const onAdd = () => {
    if (!product) return;
    addItem(product.slug, qty);
    setQty(1);
    setBagOpen(true);
  };

  useEffect(() => {
    setQty(1);
    window.scrollTo(0, 0);
    if (product) {
      document.title = `${product.name} — FOLD. Artisan Bakery`;
      document.querySelector('meta[name="description"]')?.setAttribute('content', `${product.name} from FOLD.: ${product.description}`.slice(0, 160));
    }
  }, [product]);

  const others = product ? products.filter((p) => p.id !== product.id).slice(0, 3) : [];

  return (
    <div className="fold-home">
      <SiteNav />
      <main className="pt-16 md:pt-20">
        {!product ? (
          <section className="fold-shell py-32 md:py-44">
            <p className="fold-label text-[#8A8580]">Not on the menu</p>
            <h1 className="fold-serif mt-6 text-5xl md:text-7xl">We couldn&apos;t find that bake.</h1>
            <Link to="/collection" className="mt-10 inline-block fold-label fold-link whitespace-nowrap cursor-pointer">Back to the collection</Link>
          </section>
        ) : (
          <>
            <div className="fold-shell py-4">
              <Link to="/collection" className="fold-label whitespace-nowrap cursor-pointer inline-flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity">
                <i className="ri-arrow-left-line text-sm"></i> Collection
              </Link>
            </div>
            <ProductGallery name={product.name} images={product.galleryImages.map(cdn)} />
            <ProductInfo product={product} qty={qty} onQty={setQty} onRequest={onAdd} />

            <section className="fold-shell pb-24 md:pb-32" aria-labelledby="more-title">
              <div className="flex items-baseline justify-between border-b border-[rgba(23,21,21,0.14)] pb-4">
                <h2 id="more-title" className="fold-label">Also from the oven</h2>
                <Link to="/collection" className="fold-label fold-link whitespace-nowrap cursor-pointer">All pieces</Link>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-12">
                {others.map((o, oi) => (
                  <Link key={o.id} to={`/product/${o.slug}`} className="group block cursor-pointer">
                    <div className="w-full aspect-[4/5] overflow-hidden bg-[#e4e0da]">
                      <PhotoMotion delay={[0, 0.1, 0.16][oi] ?? 0} fromScale={1.045} fromY={18}>
                        <img src={cdn(o.coverImage)} alt={o.name} title={`${o.name} — FOLD.`} className="fold-img-hover w-full h-full object-cover object-center" />
                      </PhotoMotion>
                    </div>
                    <p className="mt-4 text-[11px] tracking-[0.14em] uppercase text-[#77726c]">{o.category}</p>
                    <div className="mt-1.5 flex items-baseline justify-between gap-4">
                      <span className="fold-serif text-xl md:text-[22px] leading-tight min-w-0">{o.name}</span>
                      <span className="text-[15px] tabular-nums whitespace-nowrap">${o.price.toFixed(2)}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-[#55504b]">{o.note}</p>
                  </Link>
                ))}
              </div>
            </section>
          </>
        )}
        <VisitFooter />
      </main>
    </div>
  );
}